import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "./useAuth";

export interface UsePhoneAuthReturn {
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  verificationCode: string;
  setVerificationCode: (value: string) => void;
  timeLeft: number;
  isCodeSent: boolean;
  isLoading: boolean;
  error: string | null;
  sendCode: () => Promise<void>;
  verifyCode: () => Promise<boolean>;
  resetTimer: () => void;
  canResendCode: boolean;
  resetAuthFlow: () => void;
}

const TIMER_DURATION = 60; // 1분 = 60초

export const usePhoneAuth = (): UsePhoneAuthReturn => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 타이머 관리
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timeLeft]);

  // 타이머 리셋
  const resetTimer = useCallback(() => {
    setTimeLeft(TIMER_DURATION);
  }, []);

  // 인증 코드 발송
  const sendCode = useCallback(async () => {
    if (!phoneNumber.trim()) {
      setError("휴대폰 번호를 입력해주세요.");
      return;
    }

    // 휴대폰 번호 형식 검증 (간단한 검증)
    const phoneRegex = /^01[0-9]-?[0-9]{4}-?[0-9]{4}$/;
    if (!phoneRegex.test(phoneNumber.replace(/[-\s]/g, ""))) {
      setError("올바른 휴대폰 번호를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.getPhoneLoginCode({
        telephoneNo: phoneNumber.replace(/[-\s]/g, ""), // 하이픈과 공백 제거
      });

      if (response.success) {
        setIsCodeSent(true);
        resetTimer();
        setError(null);
      } else {
        setError(response.message || "인증 코드 발송에 실패했습니다.");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setError(error.message || "인증 코드 발송 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, resetTimer]);

  // 인증 코드 확인
  const verifyCode = useCallback(async (): Promise<boolean> => {
    if (!verificationCode.trim()) {
      setError("인증 코드를 입력해주세요.");
      return false;
    }

    if (timeLeft <= 0) {
      setError("인증 시간이 만료되었습니다. 새로운 코드를 요청해주세요.");
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 번호 인증 및 사용자 존재 여부 확인
      const response = await authService.phoneLogin({
        telephoneNo: phoneNumber.replace(/[-\s]/g, ""),
        code: verificationCode,
      });

      if (response.success && response.data) {
        if (response.data.userExists) {
          // 기존 사용자 - 로그인 처리
          if (response.data.accessToken) {
            login(response.data.accessToken, response.data.refreshToken);
            setError(null);
            return true;
          } else {
            setError("로그인 처리 중 오류가 발생했습니다.");
            return false;
          }
        } else {
          // 신규 사용자 - 회원가입 페이지로 이동
          // 번호는 세션에 저장하여 회원가입 과정에서 사용
          sessionStorage.setItem(
            "phoneNumber",
            phoneNumber.replace(/[-\s]/g, "")
          );
          navigate("/auth/terms");
          return true;
        }
      } else {
        setError(
          response.message || "인증에 실패했습니다. 코드를 확인해주세요."
        );
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setError(error.message || "인증 처리 중 오류가 발생했습니다.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, verificationCode, timeLeft, login, navigate]);

  const canResendCode = timeLeft === 0 && !isLoading;

  // 인증 플로우 초기화
  const resetAuthFlow = useCallback(() => {
    setPhoneNumber("");
    setVerificationCode("");
    setTimeLeft(0);
    setIsCodeSent(false);
    setError(null);
  }, []);

  return {
    phoneNumber,
    setPhoneNumber,
    verificationCode,
    setVerificationCode,
    timeLeft,
    isCodeSent,
    isLoading,
    error,
    sendCode,
    verifyCode,
    resetTimer,
    canResendCode,
    resetAuthFlow,
  };
};
